/**
 * Process Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 * This layer can use toast, store updates, and other UI concerns
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';
import { useProcessStore } from '@/store/process.store';
import { useTenantStore } from '@/store/tenant.store';
import { Logger } from '@/lib/app-log.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ProcessIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /** Lookup list without mutating the process admin store or toasting. */
    async queryAll(params = {}) {
        return this.service.getAll(params);
    }

    /**
     * Fetch processes and update store.
     *
     * Supports two call signatures for backwards compatibility:
     * 1) Positional args:
     *    fetchProcesses(page, pageSize, search, sortBy, sortOrder, columnFilters)
     * 2) Object arg (for selector dialogs, etc.):
     *    fetchProcesses({ page, pageSize, limit, search, sortBy, sortOrder, sortDirection, excludeIds, filter_* })
     */
    async fetchProcesses(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useProcessStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: 'Tenant context required' };
        }

        try {
            let response;
            let effectivePage = 1;
            let effectivePageSize = 10;

            if (typeof page === 'object' && page !== null) {
                // Object-style params (used by DependencySelectorDialog)
                const params = page || {};

                effectivePage = params.page ?? 1;
                effectivePageSize = params.pageSize ?? params.limit ?? 10;

                const {
                    search: objSearch = '',
                    sortBy: objSortBy = '',
                    sortOrder: objSortOrder,
                    sortDirection
                } = params;

                const finalSortOrder = objSortOrder || sortDirection || 'asc';

                // Pass through filter_ keys, excludeIds, assetId (processes linked to an asset)
                const extra = Object.entries(params).reduce((acc, [key, value]) => {
                    if (key.startsWith('filter_') || key === 'excludeIds' || key === 'assetId') {
                        acc[key] = value;
                    }
                    return acc;
                }, {});

                response = await this.service.getAll({
                    page: effectivePage,
                    pageSize: effectivePageSize,
                    limit: effectivePageSize,
                    search: objSearch,
                    sortBy: objSortBy,
                    sortOrder: finalSortOrder,
                    tenantId,
                    ...extra
                });
            } else {
                // Legacy positional arguments
                effectivePage = page;
                effectivePageSize = pageSize;

                response = await this.service.getAll({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId,
                    ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                        if (value && value.length > 0) {
                            acc[`filter_${key}`] = value;
                        }
                        return acc;
                    }, {})
                });
            }

            // With createBaseController, response.data (payload) has:
            // { success, data (array), pagination, uniqueValues }
            const { data, pagination, uniqueValues } = response;
            store.setProcesses(data || []);

            // Map pagination from backend response payload
            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                store.setPaginationData({
                    currentPage: effectivePage,
                    totalPages: Math.ceil((data?.length || 0) / effectivePageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: effectivePageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return {
                success: true,
                data: data || [],
                pagination: pagination || {},
                uniqueValues: uniqueValues || {}
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch processes for dropdown
     */
    async fetchProcessesForDropdown() {
        const store = useProcessStore.getState();
        try {
            const result = await fetchDropdownOptionsFromReference('process', async () => {
                const response = await this.service.getDropdownParams();
                return { success: true, data: response.data || [] };
            });
            store.setProcessesForDropdown(result.data || []);
        } catch (error) {
            Logger.error('Failed to fetch process dropdown', { error: error?.message });
        }
    }

    /**
     * Create process
     */
    async createProcess(data) {
        const store = useProcessStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: 'Tenant context required' };
        }

        try {
            const response = await this.service.create(data);

            toast.success(i18next.t('generalSystemProcess:messages.createSuccess', {
                defaultValue: 'Process created',
            }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update process
     */
    async updateProcess(id, data, opts = {}) {
        const store = useProcessStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (!opts.skipStoreLoading) store.setLoading(true);

        if (!tenantId) {
            if (!opts.skipStoreLoading) store.setLoading(false);
            return { success: false, error: 'Tenant context required' };
        }

        try {
            const response = await this.service.update(id, data);

            if (!opts.skipSuccessToast) {
                toast.success(i18next.t('generalSystemProcess:messages.updateSuccess', {
                    defaultValue: 'Process updated',
                }));
            }

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            if (!opts.skipStoreLoading) store.setLoading(false);
        }
    }

    /**
     * Delete process
     */
    async deleteProcess(id) {
        const store = useProcessStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: 'Tenant context required' };
        }

        try {
            await this.service.delete(id);

            toast.success(i18next.t('generalSystemProcess:messages.deleteSuccess', {
                defaultValue: 'Process deleted',
            }));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete processes
     * @param {Array<string>} ids
     */
    async bulkDeleteProcesses(ids) {
        const store = useProcessStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: 'Tenant context required' };
        }

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('generalSystemProcess:messages.bulkDeleteSuccess', {
                count: ids.length,
                defaultValue: '{{count}} processes deleted',
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Process list for asset detail / selectors without mutating the global process store.
     */
    async fetchListDetached(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: [], meta: { total: 0 }, uniqueValues: {} };
        }
        try {
            const response = await this.service.getAll({
                page: params.page ?? 1,
                pageSize: params.pageSize ?? params.limit ?? 10,
                limit: params.limit ?? params.pageSize ?? 10,
                search: params.search ?? '',
                sortBy: params.sortBy ?? 'name',
                sortOrder: params.sortOrder ?? params.sortDirection ?? 'asc',
                tenantId,
                ...(params.assetId ? { assetId: params.assetId } : {}),
                ...(params.excludeIds != null && params.excludeIds !== ''
                    ? { excludeIds: params.excludeIds }
                    : {}),
                ...Object.entries(params).reduce((acc, [key, value]) => {
                    if (key.startsWith('filter_')) acc[key] = value;
                    return acc;
                }, {}),
            });
            const pagination = response.pagination || {};
            return {
                success: true,
                data: response.data || [],
                meta: {
                    total: pagination.total ?? 0,
                    page: pagination.page ?? 1,
                    pages: pagination.totalPages ?? 0,
                },
                uniqueValues: response.uniqueValues || {},
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                meta: { total: 0 },
                uniqueValues: {},
                error: this._getErrorMessage(error),
            };
        }
    }

    /**
     * Map error to user-friendly message
     */
    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundKey: 'generalSystemProcess:messages.notFoundError',
            notFoundDefault: 'Process not found',
        });
    }
}
